import React, {useContext, useEffect, useState} from 'react';
import Helmet from 'react-helmet';
import {useStores} from '../../../hooks';
import DocumentsStoreContext from '../context';
import {Button, Paper, Typography} from '../../../common/components/libs';
import {Link, Table, TableBody, TableCell, TableContainer, TableRow} from '@material-ui/core';
import {EnhancedTableHead, HeadCell} from '../../../common/components/TableHelpers';
import {CardServiceOrder, InvoiceServiceOrder} from '../../../services/organizationBranches/models';
import {ServiceOrderStatus, TariffPeriod, TariffVariant} from '../models';
import moment from 'moment';
import {Delete} from '@material-ui/icons';
import {SnackbarType} from '../../../stores/common';
import {observer} from 'mobx-react';
import {ConfirmCancelInvoiceOrderForm} from '../components/forms/ConfirmCancelInvoiceOrderForm';

const PaymentsHistoryPage = () => {
    const { generalStore, constantsStore } = useStores();
    const documentsStore = useContext(DocumentsStoreContext);
    const currencyFormatter = constantsStore.getCurrencyFormatter('RUB');
    const { serviceOrders, isServiceOrdersLoaded } = documentsStore;
    const [open, setOpen] = useState<boolean>(false)
    const [chosenInvoice, setChosenInvoice] = useState<InvoiceServiceOrder | null>(null)

    useEffect(() => {
        generalStore.setPageTitle("История оплат");
        documentsStore.getOrganisationBranch();
        documentsStore.getServiceOrders();
    }, [generalStore, documentsStore]);

    const headCells: Array<HeadCell> = [
        { id: 'id', alignment: 'left', label:  "#" },
        { id: 'type', alignment: 'left', label: "Тип" },
        { id: 'tariff', alignment: 'left', label: "Тариф" },
        { id: 'amount', alignment: 'left', label: "Сумма" },
        { id: 'status', alignment: 'left', label: "Статус" },
        { id: 'link', alignment: 'left', label: "Ссылка" },
        { id: 'date', alignment: 'left', label: "Дата"},
        { id: 'action', alignment: 'left', label: ''}
    ];

    const paymentTypes = {
        Card: 'Карта',
        Invoice: 'Счёт'
    }

    const colorByStatus = {
        New: '#f57c00',
        Cancelled: '#f57c00',
        Error: '#dc004e',
        Paid: '#388e3c'
    }

    const cancelInvoiceOrder = async (order: InvoiceServiceOrder) => {
        const result = await documentsStore.cancelInvoiceOrder({
            invoiceId: order.invoiceId
        })

        if (!result) {
            generalStore.showSnackbar({ type: SnackbarType.error, title: 'Не удалось удалить счёт' })
        } else {
            generalStore.showSnackbar({ type: SnackbarType.success, title: 'Счёт удалён' })
        }
        setChosenInvoice(null)
        setOpen(false)
    }

    const getLink = (order: CardServiceOrder | InvoiceServiceOrder) => {
        if (order.type == 'Card') {
            order as CardServiceOrder
            return order.receiptLink ? <Link href={order.receiptLink} target="_blank" variant="caption">
                    Чек
                </Link> :
                <p>-</p>
        } else {
            order as InvoiceServiceOrder
            return order.invoiceLink ? <Link href={order.invoiceLink} target="_blank" variant="caption">
                    Скачать
                </Link> :
                <p>-</p>
        }
    }

    const getStatus = (status: ServiceOrderStatus) => {
        const statusText = {
            New: 'Не оплачен',
            Cancelled: 'Отменен',
            Paid: 'Оплачен',
            Error: 'Ошибка'
        }

        return statusText[status]
    }

    const getDate = (order: CardServiceOrder | InvoiceServiceOrder) => {
        if (order.status === 'Paid') {
            return order.paidAt ? moment(order.paidAt).format('DD.MM.YYYY HH:MM') : ''
        } else {
            return moment(order.createdAt).format('DD.MM.YYYY HH:MM')
        }
    }

    const getTariff = (tariff: TariffVariant, period: TariffPeriod) => {
        let tariffStr = ''

        switch (tariff) {
            case 'Simple': {
                tariffStr = `Простой x `
                break
            }
            case 'Basic': {
                tariffStr = 'Базовый x '
                break
            }
            case 'Advanced': {
                tariffStr = 'Расширенный x '
                break
            }
        }

        switch (period) {
            case 'Month': {
                tariffStr += '1 мес'
                break
            }
            case 'HalfYear': {
                tariffStr += '6 мес'
                break
            }
            case 'Year': {
                tariffStr += '12 мес'
                break
            }
        }

        return tariffStr
    }

    return <>
        <Helmet title="История оплат">
        </Helmet>
        <ConfirmCancelInvoiceOrderForm open={open} invoice={chosenInvoice} onSubmit={() => cancelInvoiceOrder(chosenInvoice)} onClose={() => {
            setOpen(false)
            setChosenInvoice(null)
        }}/>
        <Typography mb={2} component="h1" variant="h2" align="left">
            История оплат
        </Typography>
        <Paper p={5}>
            <TableContainer>
                <Table
                    aria-labelledby="tableTitle"
                    size={"medium"}
                    aria-label="enhanced table"
                >
                    <EnhancedTableHead cells={headCells}/>
                    <TableBody>
                        {serviceOrders.map((order, index) => (
                            <TableRow
                                hover
                                tabIndex={-1}
                                key={`${order.type}-${index}`}
                            >
                                <TableCell align="left">
                                    <Typography variant={'caption'}>
                                        {order.type === 'Card' ? `S${(order as CardServiceOrder).orderId}` : (order as InvoiceServiceOrder).invoiceId}
                                    </Typography>
                                </TableCell>
                                <TableCell align="left">
                                    <Typography variant={'caption'}>
                                        {paymentTypes[order.type]}
                                    </Typography>
                                </TableCell>
                                <TableCell align="left">
                                    <Typography variant={'caption'}>
                                        {getTariff(order.tariff, order.period)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="left">
                                    <Typography variant={'caption'}>
                                        {currencyFormatter.toShow(order.price.price)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="left" style={{ color: colorByStatus[order.status] }}>
                                    <Typography variant={'caption'}>
                                        {getStatus(order.status)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="left">
                                    {getLink(order)}
                                </TableCell>
                                <TableCell align="left">
                                    <Typography variant={'caption'}>
                                        {getDate(order)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="left">
                                    {order.type == 'Invoice' && order.status == 'New' ? (
                                        <Button variant={'text'} onClick={() => {
                                            setChosenInvoice(order as InvoiceServiceOrder)
                                            setOpen(true)
                                        }}>
                                            <Delete style={{ color: 'red' }}/>
                                        </Button>
                                    ) : ''}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    </>
}

export default observer(PaymentsHistoryPage)